import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Badge } from 'react-bootstrap';
import {
    FaEdit, FaSave, FaTimes, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt,
    FaCalendarAlt, FaVenusMars, FaIdCard, FaTrash, FaUserShield, FaKey,
    FaGlobeAmericas, FaInfoCircle, FaLock, FaEye, FaEyeSlash
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RoleService from '../../../services/RoleService';
import HabilitationService from '../../../services/HabilitationService';
import PhoneNumberInput from "../../../utils/PhoneNumberInput";
import CountryInput from "../../../utils/CountryInput";
import CityInput from "../../../utils/AdminCityInput";
import NeighborhoodInput from "../../../utils/NeighborhoodInput";
import LoadingSpinner from '../../../utils/LoadingSpinner';

const FieldBox = ({ label, icon, children }) => (
    <div className="mb-3">
        <div className="d-flex align-items-center mb-2 text-secondary fw-semibold" style={{ fontSize: '0.85rem' }}>
            <span className="me-2 d-flex">{icon}</span>{label}
        </div>
        <div className="border rounded-3 px-3 py-2 bg-white" style={{ minHeight: '42px' }}>
            {children}
        </div>
    </div>
);

const Section = ({ title, icon, color, children }) => (
    <div className="rounded-3 p-4 mb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <h5 className="fw-bold mb-4 d-flex align-items-center" style={{ color }}>
            <span className="me-2 d-flex">{icon}</span>{title}
        </h5>
        {children}
    </div>
);

// Fonction utilitaire pour vérifier les règles du mot de passe
const getPasswordErrors = (password) => {
    const errs = [];
    if (!password || password.length < 6) errs.push('Au moins 6 caractères');
    if (!password || !/[a-z]/.test(password)) errs.push('Au moins une lettre minuscule');
    if (!password || !/[A-Z]/.test(password)) errs.push('Au moins une lettre majuscule');
    if (!password || !/[0-9]/.test(password)) errs.push('Au moins un chiffre');
    if (!password || !/[^a-zA-Z0-9]/.test(password)) errs.push('Au moins un caractère spécial');
    return errs;
};

const UserDetailModal = ({ user, onClose, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    // On initialise password et confirmPassword à vide
    const [editedUser, setEditedUser] = useState({ ...user, password: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [allRoles, setAllRoles] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [allHabilitations, setAllHabilitations] = useState([]);
    const [availableHabilitations, setAvailableHabilitations] = useState([]);
    const [loading, setLoading] = useState(true);

    // États pour l'affichage des mots de passe
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [rolesResponse, habilitationsResponse] = await Promise.all([
                    RoleService.findAllRoles(),
                    HabilitationService.getAllHabilitations()
                ]);

                setAllRoles(rolesResponse);
                setAvailableRoles(rolesResponse.filter(role =>
                    !user.roles.some(userRole => userRole.id === role.id)
                ));

                setAllHabilitations(habilitationsResponse);
                setAvailableHabilitations(habilitationsResponse.filter(habilitation =>
                    !user.habilitations.some(userHabilitation => userHabilitation.name === habilitation.name)
                ));

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Erreur lors du chargement des données initiales');
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'Non spécifié';
        try {
            return dateString.split(' ')[0];
        } catch {
            return dateString;
        }
    };

    const formatDateForBackend = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}-${month}-${year} ${hours}:${minutes}`;
        } catch {
            return dateString;
        }
    };

    const validateField = (name, value, currentState) => {
        let error = '';
        const stateToCheck = currentState || editedUser;

        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'email':
                if (!value.trim()) {
                    error = 'Ce champ est obligatoire';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Email invalide';
                }
                break;
            case 'phoneNumber':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            case 'neighborhood':
            case 'city':
            case 'country':
                if (!value || !value.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'dateOfBirth':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                } else {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const birthDate = new Date(value.includes('T') ? value : formatDateForBackend(value));
                    birthDate.setHours(0, 0, 0, 0);
                    if (birthDate > today) error = 'La date ne peut pas être dans le futur';
                }
                break;
            case 'gender':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            case 'password':
                // En édition, le mot de passe n'est obligatoire que si l'utilisateur le remplit
                if (value) {
                    return getPasswordErrors(value); // Retourne un tableau d'erreurs
                }
                return []; // Retourne un tableau vide si non rempli
            case 'confirmPassword':
                // La confirmation doit correspondre si l'un des deux est rempli
                if (value !== stateToCheck.password) {
                    error = 'Les mots de passe ne correspondent pas';
                }
                break;
            default:
                break;
        }
        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedUser = { ...editedUser, [name]: value };

        const error = validateField(name, value, updatedUser);
        const newErrors = { ...errors, [name]: error };

        // Si on modifie le mot de passe, on revalide la confirmation
        if (name === 'password') {
            const confirmError = validateField('confirmPassword', updatedUser.confirmPassword, updatedUser);
            newErrors.confirmPassword = confirmError;
        }

        setErrors(newErrors);
        setEditedUser(updatedUser);
    };

    // --- Handlers pour les composants réutilisables ---
    const handlePhoneNumberChange = (value) => {
        setEditedUser(prev => ({ ...prev, phoneNumber: value }));
        setErrors(prev => ({ ...prev, phoneNumber: null }));
    };

    const handlePhoneValidation = (error) => {
        setErrors(prev => ({ ...prev, phoneNumber: error }));
    };

    const handleCountryChange = (value) => {
        setEditedUser(prev => ({ ...prev, country: value }));
        setErrors(prev => ({ ...prev, country: null }));
    };

    const handleCountryValidation = (error) => {
        setErrors(prev => ({ ...prev, country: error }));
    };

    const handleCityChange = (value) => {
        setEditedUser(prev => ({ ...prev, city: value }));
        setErrors(prev => ({ ...prev, city: null }));
    };

    const handleCityValidation = (error) => {
        setErrors(prev => ({ ...prev, city: error }));
    };

    const handleNeighborhoodChange = (value) => {
        setEditedUser(prev => ({ ...prev, neighborhood: value }));
        setErrors(prev => ({ ...prev, neighborhood: null }));
    };

    const handleNeighborhoodValidation = (error) => {
        setErrors(prev => ({ ...prev, neighborhood: error }));
    };

    const handleRoleSelect = (e) => {
        const selectedRoleId = parseInt(e.target.value);
        if (selectedRoleId === 0) return;
        const selectedRole = allRoles.find(role => role.id === selectedRoleId);
        if (selectedRole && !editedUser.roles.some(role => role.id === selectedRoleId)) {
            setEditedUser(prev => ({ ...prev, roles: [...prev.roles, selectedRole] }));
            setAvailableRoles(prev => prev.filter(role => role.id !== selectedRoleId));
            setErrors(prev => ({ ...prev, roles: undefined }));
        }
    };

    const removeRole = (roleId) => {
        setEditedUser(prev => ({ ...prev, roles: prev.roles.filter(role => role.id !== roleId) }));
        const roleToAddBack = allRoles.find(role => role.id === roleId);
        if (roleToAddBack) setAvailableRoles(prev => [...prev, roleToAddBack]);
    };

    const handleHabilitationSelect = (e) => {
        const selectedHabilitationName = e.target.value;
        if (selectedHabilitationName === "") return;
        const selectedHabilitation = allHabilitations.find(h => h.name === selectedHabilitationName);
        if (selectedHabilitation && !editedUser.habilitations.some(h => h.name === selectedHabilitationName)) {
            setEditedUser(prev => ({ ...prev, habilitations: [...prev.habilitations, selectedHabilitation] }));
            setAvailableHabilitations(prev => prev.filter(h => h.name !== selectedHabilitationName));
        }
    };

    const removeHabilitation = (habilitationName) => {
        setEditedUser(prev => ({ ...prev, habilitations: prev.habilitations.filter(h => h.name !== habilitationName) }));
        const habilitationToAddBack = allHabilitations.find(h => h.name === habilitationName);
        if (habilitationToAddBack) setAvailableHabilitations(prev => [...prev, habilitationToAddBack]);
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(editedUser).forEach(field => {
            if (!['profilePictureUrl', 'additionalInfos', 'habilitations', 'useRef', 'username', 'phoneNumber', 'country', 'city', 'neighborhood'].includes(field)) {
                const error = validateField(field, editedUser[field], editedUser);
                // Gestion spécifique pour le tableau d'erreurs du mot de passe
                if (field === 'password') {
                    if (error && error.length > 0) {
                        newErrors[field] = error;
                        isValid = false;
                    }
                } else if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            }
        });

        // Vérification des erreurs provenant des composants réutilisables
        if (errors.phoneNumber) { newErrors.phoneNumber = errors.phoneNumber; isValid = false; }
        if (errors.country) { newErrors.country = errors.country; isValid = false; }
        if (errors.city) { newErrors.city = errors.city; isValid = false; }
        if (errors.neighborhood) { newErrors.neighborhood = errors.neighborhood; isValid = false; }

        if (editedUser.roles.length === 0) {
            newErrors.roles = 'Au moins un rôle doit être sélectionné';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = () => {
        if (!validateForm()) {
            const firstErrorField = Object.keys(errors).find(key => {
                if (key === 'password') return errors[key].length > 0;
                return errors[key];
            });
            if (firstErrorField) {
                const element = document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.focus();
                } else if (firstErrorField === 'roles') {
                    document.querySelector('.roles-section select')?.focus();
                }
            }
            return;
        }

        // On retire confirmPassword avant l'envoi à l'API
        const { confirmPassword, ...userDataToSend } = editedUser;

        const userToSave = {
            firstName: userDataToSend.firstName,
            lastName: userDataToSend.lastName,
            email: userDataToSend.email,
            phoneNumber: userDataToSend.phoneNumber,
            neighborhood: userDataToSend.neighborhood,
            city: userDataToSend.city,
            country: userDataToSend.country,
            username: userDataToSend.username,
            additionalInfos: userDataToSend.additionalInfos,
            roleIds: userDataToSend.roles.map(role => role.id),
            habilitationNames: userDataToSend.habilitations.map(h => h.name),
            dateOfBirth: formatDateForBackend(userDataToSend.dateOfBirth),
            gender: userDataToSend.gender,
            lastModifiedBy: 'admin'
        };

        // On n'envoie le mot de passe que s'il a été rempli (pour ne pas écraser l'ancien avec une chaîne vide)
        if (userDataToSend.password) {
            userToSave.password = userDataToSend.password;
        }

        onSave(editedUser.id, userToSave);
        setIsEditing(false);
    };

    const renderField = (label, value, icon, fieldName, type = 'text') => {
        const error = errors[fieldName];

        if (fieldName === 'username') {
            return (
                <FieldBox label={label} icon={icon}>
                    <div className="d-flex align-items-center justify-content-between text-muted">
                        <span>{value || 'Non spécifié'}</span>
                        <FaLock size={12} title="Non modifiable" />
                    </div>
                </FieldBox>
            );
        }

        if (fieldName === 'useRef') {
            return (
                <FieldBox label={label} icon={icon}>
                    <span className="text-muted">{value || 'Non spécifié'}</span>
                </FieldBox>
            );
        }

        if (fieldName === 'gender') {
            return (
                <FieldBox label={label} icon={icon}>
                    {isEditing ? (
                        <>
                            <select
                                name={fieldName}
                                value={value || ''}
                                onChange={handleInputChange}
                                className={`form-select form-select-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                            >
                                <option value="">Sélectionnez un genre</option>
                                <option value="FEMALE">Femme</option>
                                <option value="MALE">Homme</option>
                            </select>
                            {error && <div className="invalid-feedback d-block small">{error}</div>}
                        </>
                    ) : (
                        <span>{value === 'FEMALE' ? 'Femme' : value === 'MALE' ? 'Homme' : 'Non spécifié'}</span>
                    )}
                </FieldBox>
            );
        }

        return (
            <FieldBox label={label} icon={icon}>
                {isEditing ? (
                    type === 'textarea' ? (
                        <textarea
                            name={fieldName}
                            value={value || ''}
                            onChange={handleInputChange}
                            className={`form-control form-control-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                            rows={3}
                        />
                    ) : fieldName === 'dateOfBirth' ? (
                        <input
                            type="date"
                            name={fieldName}
                            value={value ? formatDateForDisplay(value) : ''}
                            onChange={handleInputChange}
                            className={`form-control form-control-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                        />
                    ) : (
                        <input
                            type={type}
                            name={fieldName}
                            value={value || ''}
                            onChange={handleInputChange}
                            className={`form-control form-control-sm border-0 p-0 ${error ? 'is-invalid' : ''}`}
                        />
                    )
                ) : (
                    <span>{fieldName === 'dateOfBirth' ? formatDateForDisplay(value) : (value || 'Non spécifié')}</span>
                )}
                {error && <div className="invalid-feedback d-block small">{error}</div>}
            </FieldBox>
        );
    };

    if (loading) {
        return (
            <div className="user-modal-overlay">
                <div
                    className="user-modal-content"
                    style={{
                        borderTopLeftRadius: '20px',
                        borderTopRightRadius: '20px',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '300px'
                    }}
                >
                    <LoadingSpinner
                        isVisible={true}
                        loadingText="Chargement des données..."
                        position="absolute"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="user-modal-overlay">
            <div
                className="user-modal-content"
                style={{
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    overflow: 'hidden'
                }}
            >
                {/* En-tête */}
                <div className="d-flex justify-content-between align-items-start px-4 py-3 border-bottom bg-white flex-shrink-0">
                    <div className="d-flex align-items-start">
                        <FaUser size={22} className="text-primary me-3 mt-1" />
                        <div>
                            <h4 className="fw-bold mb-0">
                                {isEditing ? 'Modifier utilisateur' : 'Détails de l\'utilisateur'}
                            </h4>
                            <small className="text-muted">
                                Référence : {editedUser.useRef || 'Non spécifié'}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {!isEditing ? (
                            <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                                <FaEdit className="me-1" /> Modifier
                            </Button>
                        ) : (
                            <>
                                <Button variant="success" size="sm" onClick={handleSave}>
                                    <FaSave className="me-1" /> Enregistrer
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => setIsEditing(false)}>
                                    <FaTimes className="me-1" /> Annuler
                                </Button>
                            </>
                        )}
                        <FaTimes
                            size={22}
                            role="button"
                            className="text-secondary ms-2"
                            onClick={onClose}
                        />
                    </div>
                </div>

                {/* Corps scrollable */}
                <div className="user-modal-body">
                    <div className="container-fluid">
                        {/* Informations personnelles */}
                        <Section title="Informations Personnelles" icon={<FaUser />} color="#6f42c1">
                            <Row className="mb-3">
                                <Col md="auto">
                                    <img
                                        src="/assets/images/team/avatar.png"
                                        alt={`${editedUser.firstName} ${editedUser.lastName}`}
                                        className="rounded-circle border border-3 border-primary"
                                        style={{ width: '90px', height: '90px', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.src = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/profile/avatar.jpg';
                                        }}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>{renderField('Prénom', editedUser.firstName, <FaUser />, 'firstName')}</Col>
                                <Col md={6}>{renderField('Nom', editedUser.lastName, <FaUser />, 'lastName')}</Col>
                                <Col md={6}>{renderField('Email', editedUser.email, <FaEnvelope />, 'email', 'email')}</Col>
                                <Col md={6}>{renderField('Date de naissance', editedUser.dateOfBirth, <FaCalendarAlt />, 'dateOfBirth', 'date')}</Col>
                                <Col md={6}>{renderField('Genre', editedUser.gender, <FaVenusMars />, 'gender')}</Col>
                                <Col md={6}>{renderField('Référence', editedUser.useRef, <FaIdCard />, 'useRef')}</Col>
                            </Row>
                        </Section>

                        {/* Informations de connexion */}
                        <Section title="Informations de Connexion" icon={<FaKey />} color="#0d6efd">
                            <Row>
                                <Col md={6}>{renderField('Username', editedUser.username, <FaUser />, 'username')}</Col>

                                {/* Champ Mot de passe (Visible uniquement en édition) */}
                                <Col md={6}>
                                    {isEditing ? (
                                        <FieldBox label="Nouveau mot de passe" icon={<FaLock />}>
                                            <div className="d-flex align-items-center w-100">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    name="password"
                                                    value={editedUser.password || ''}
                                                    onChange={handleInputChange}
                                                    className={`form-control form-control-sm border-0 p-0 ${errors.password && errors.password.length > 0 ? 'is-invalid' : ''}`}
                                                    placeholder="Laisser vide pour ne pas changer"
                                                />
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-secondary p-0 ms-2"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </Button>
                                            </div>
                                            {/* Affichage dynamique de la liste des règles non satisfaites */}
                                            {errors.password && errors.password.length > 0 && (
                                                <div className="invalid-feedback d-block small">
                                                    <ul className="mb-0 ps-3">
                                                        {errors.password.map((err, idx) => (
                                                            <li key={idx}>{err}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </FieldBox>
                                    ) : (
                                        <FieldBox label="Mot de passe" icon={<FaLock />}>
                                            <div className="d-flex align-items-center justify-content-between text-muted">
                                                <span>••••••••</span>
                                                <FaLock size={12} title="Non modifiable" />
                                            </div>
                                        </FieldBox>
                                    )}
                                </Col>

                                {/* Champ Confirmation Mot de passe (Visible uniquement en édition) */}
                                {isEditing && (
                                    <Col md={6}>
                                        <FieldBox label="Confirmer le mot de passe" icon={<FaLock />}>
                                            <div className="d-flex align-items-center w-100">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    name="confirmPassword"
                                                    value={editedUser.confirmPassword || ''}
                                                    onChange={handleInputChange}
                                                    className={`form-control form-control-sm border-0 p-0 ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                    placeholder="Confirmez le nouveau mot de passe"
                                                />
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-secondary p-0 ms-2"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                >
                                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                </Button>
                                            </div>
                                            {errors.confirmPassword && <div className="invalid-feedback d-block small">{errors.confirmPassword}</div>}
                                        </FieldBox>
                                    </Col>
                                )}
                            </Row>
                        </Section>

                        {/* Localisation */}
                        <Section title="Localisation" icon={<FaMapMarkerAlt />} color="#20c997">
                            <Row className="g-3">
                                {/* 1. Pays */}
                                <Col md={6}>
                                    {isEditing ? (
                                        <CountryInput
                                            value={editedUser.country}
                                            onChange={handleCountryChange}
                                            onValidation={handleCountryValidation}
                                            required={true}
                                        />
                                    ) : (
                                        <FieldBox label="Pays" icon={<FaGlobeAmericas />}>
                                            <span>{editedUser.country || 'Non spécifié'}</span>
                                        </FieldBox>
                                    )}
                                </Col>

                                {/* 2. Ville */}
                                <Col md={6}>
                                    {isEditing ? (
                                        <CityInput
                                            value={editedUser.city}
                                            onChange={handleCityChange}
                                            onValidation={handleCityValidation}
                                            required={true}
                                        />
                                    ) : (
                                        <FieldBox label="Ville" icon={<FaMapMarkerAlt />}>
                                            <span>{editedUser.city || 'Non spécifié'}</span>
                                        </FieldBox>
                                    )}
                                </Col>

                                {/* 3. Quartier */}
                                <Col md={6}>
                                    {isEditing ? (
                                        <NeighborhoodInput
                                            value={editedUser.neighborhood}
                                            onChange={handleNeighborhoodChange}
                                            onValidation={handleNeighborhoodValidation}
                                            required={true}
                                        />
                                    ) : (
                                        <FieldBox label="Quartier" icon={<FaMapMarkerAlt />}>
                                            <span>{editedUser.neighborhood || 'Non spécifié'}</span>
                                        </FieldBox>
                                    )}
                                </Col>

                                {/* 4. Téléphone */}
                                <Col md={6}>
                                    {isEditing ? (
                                        <div className="phone-input-container">
                                            <PhoneNumberInput
                                                value={editedUser.phoneNumber}
                                                onChange={handlePhoneNumberChange}
                                                onValidation={handlePhoneValidation}
                                                defaultCountry="CM"
                                            />
                                        </div>
                                    ) : (
                                        <FieldBox label="Téléphone" icon={<FaPhone />}>
                                            <span>{editedUser.phoneNumber || 'Non spécifié'}</span>
                                        </FieldBox>
                                    )}
                                </Col>
                            </Row>
                        </Section>

                        {/* Autres informations */}
                        <Section title="Autres Informations" icon={<FaInfoCircle />} color="#fd7e14">
                            <Row>
                                <Col md={12}>{renderField('Informations supplémentaires', editedUser.additionalInfos, <FaUser />, 'additionalInfos', 'textarea')}</Col>
                            </Row>
                        </Section>

                        {/* Rôles et habilitations */}
                        <Section title="Rôles et Habilitations" icon={<FaUserShield />} color="#dc3545">
                            {isEditing ? (
                                <>
                                    <div className="mb-4 roles-section">
                                        <h6 className="fw-semibold mb-2">Rôles</h6>
                                        {errors.roles && editedUser.roles.length === 0 && (
                                            <div className="alert alert-danger py-2">{errors.roles}</div>
                                        )}
                                        <select
                                            onChange={handleRoleSelect}
                                            className={`form-select mb-3 ${errors.roles ? 'is-invalid' : ''}`}
                                            defaultValue={0}
                                            disabled={availableRoles.length === 0}
                                        >
                                            <option value={0}>Sélectionnez un rôle</option>
                                            {availableRoles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name} - {role.description}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="d-flex flex-wrap gap-2">
                                            {editedUser.roles.map(role => (
                                                <Badge key={role.id} bg="primary" className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill">
                                                    {role.name}
                                                    <FaTrash
                                                        size={11}
                                                        role="button"
                                                        onClick={() => removeRole(role.id)}
                                                    />
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h6 className="fw-semibold mb-2">Habilitations</h6>
                                        <select
                                            onChange={handleHabilitationSelect}
                                            className="form-select mb-3"
                                            defaultValue=""
                                            disabled={availableHabilitations.length === 0}
                                        >
                                            <option value="">Sélectionnez une habilitation</option>
                                            {availableHabilitations.map(habilitation => (
                                                <option key={habilitation.name} value={habilitation.name}>
                                                    {habilitation.name} ({habilitation.type} - {habilitation.action})
                                                </option>
                                            ))}
                                        </select>
                                        <div className="d-flex flex-wrap gap-2">
                                            {editedUser.habilitations.map(habilitation => (
                                                <Badge key={habilitation.name} bg="secondary" className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill">
                                                    {habilitation.name}
                                                    <FaTrash
                                                        size={11}
                                                        role="button"
                                                        onClick={() => removeHabilitation(habilitation.name)}
                                                    />
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <h6 className="fw-semibold mb-2">Rôles</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {user.roles.map(role => (
                                                <Badge key={role.id} bg="primary" className="px-3 py-2 rounded-pill" title={role.description}>
                                                    {role.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h6 className="fw-semibold mb-2">Habilitations</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {user.habilitations.map(habilitation => (
                                                <Badge key={habilitation.id} bg="secondary" className="px-3 py-2 rounded-pill">
                                                    {habilitation.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </Section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailModal;